import React, { useCallback, useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Container,
  IconButton,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  Tooltip,
  Typography,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Alert,
  DialogContent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { ApiAppCustomDop } from '../../../../../api/hexa/ApiAppCustomDop';
import { ContentCopy, Delete, Save } from '@mui/icons-material';
import ConfirmContent from '../../../../components/ConfirmContent';
import { HexaAppCustomDop } from '../../../../../api/hexa/models/CustomDopModels';
import { useNavigate } from 'react-router';

interface ConsumerDetailProps {
  customerId: string | number;
  applicationId: string | number;
}

const AppCustomDopList = (props: ConsumerDetailProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerId, applicationId } = props;
  const [state, setState] = React.useState(true);
  const [open, setOpen] = React.useState({
    copySnackbar: false,
    errorSnackbar: false,
    selectorDialog: false,
    deleteDialog: false,
  });
  const selectRef = React.useRef<HTMLDivElement>(null);
  const confirmRef = React.useRef<HTMLDivElement>(null);
  const nodeRef = state ? selectRef : confirmRef;
  const [customDopToRemove, setCustomDopToRemove] = React.useState<number>();
  const [selectedDopIds, setSelectedDopIds] = useState<number[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [customDops, setCustomDops] = React.useState([] as HexaAppCustomDop[]);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [selectorCustomDops, setSelectorCustomDops] = React.useState(
    [] as HexaAppCustomDop[]
  );
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const columns: GridColDef<HexaAppCustomDop>[] = [
    {
      field: 'id',
      headerName: t('applications.detail.custom_dop.id'),
      flex: 1,
      maxWidth: 100,
    },
    {
      field: 'description',
      headerName: t('applications.detail.custom_dop.description'),
      flex: 1,
    },
    {
      field: 'file_name',
      headerName: t('applications.detail.custom_dop.file_name'),
      flex: 2,
      renderCell: (params: any) => {
        return (
          <Stack
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              gap: '0.3rem',
            }}
          >
            <Typography variant={'body2'}>{params.row.file_name}</Typography>
            <Tooltip title={t('applications.detail.custom_dop.copy')}>
              <IconButton
                aria-label="copy to clipboard"
                color="primary"
                onClick={() => copyFileName(params.row.file_name)}
              >
                <ContentCopy sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={
            <Tooltip title={t('applications.detail.custom_dop.remove')}>
              <Delete color="primary" />
            </Tooltip>
          }
          onClick={() => {
            setCustomDopToRemove(params.row.id);
            setOpen({ ...open, deleteDialog: true });
          }}
          label={t('applications.detail.custom_dop.remove')}
        />,
      ],
    },
  ];

  /**
   * Closes the copy and error snackbars
   * @param event
   * @param reason
   * @returns
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen({ ...open, copySnackbar: false, errorSnackbar: false });
  };

  /**
   * Close the selector dialog, reset the selected DOPs and the dialog state
   */
  const handleSelectorDialogClose = () => {
    setOpen({ ...open, selectorDialog: false });
    setSelectedDopIds([]);
    if (!state) {
      setState(true);
    }
  };

  /**
   * Reset the dialog state (shows the initial form) and discard the previous selection
   */
  const onDiscardClick = () => {
    setState((state) => !state);
    setSelectedDopIds([]);
  };

  /**
   * Copy a DOP file name and shows an info snackbar
   */
  const copyFileName = (fileName: any) => {
    navigator.clipboard.writeText(fileName);
    setOpen({ ...open, copySnackbar: true });
  };

  /**
   * Fetch the custom DOPs linked to the application from the API
   */
  const fetchCustomDops = useCallback(async () => {
    setLoading(true);
    try {
      const { total, custom_dop } = await ApiAppCustomDop.index(
        customerId,
        applicationId,
        {
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }
      );
      setRowCount(total);
      setCustomDops(custom_dop);
    } catch (error: any) {
      setError(
        `Error ${error.status ?? 500} fetching application custom DOP: 
        ${error.response?.data.error ?? 'Error fetching custom DOPs'}`
      );
      setOpen((prevState) => ({ ...prevState, errorSnackbar: true }));
    } finally {
      setLoading(false);
    }
  }, [
    customerId,
    applicationId,
    paginationModel.page,
    paginationModel.pageSize,
  ]);

  useEffect(() => {
    fetchCustomDops();
  }, [fetchCustomDops]);

  /**
   * Fetch all the custom DOPs not linked to the application from the API
   */
  const fetchSelectorCustomDops = useCallback(async () => {
    setLoading(true);
    try {
      const { custom_dop } = await ApiAppCustomDop.index(
        customerId,
        applicationId,
        {
          exclude: true,
        }
      );
      setSelectorCustomDops(custom_dop);
    } catch (error: any) {
      setOpen((prevState) => ({ ...prevState, errorSnackbar: true }));
      setError(
        `Error ${error.status ?? 500} fetching all custom DOP:
        ${error.response?.data.error ?? 'Error fetching custom DOPs'}`
      );
    } finally {
      setLoading(false);
    }
  }, [customerId, applicationId]);

  useEffect(() => {
    fetchSelectorCustomDops();
  }, [fetchSelectorCustomDops]);

  /**
   * Add the selected custom DOPs to the application, close and refresh the dialog
   */
  const onSubmit = useCallback(async () => {
    setLoading(true);
    await ApiAppCustomDop.create(customerId, applicationId, selectedDopIds)
      .then(() => {
        fetchCustomDops();
        fetchSelectorCustomDops();
      })
      .catch((error) => {
        setError(
          `Error ${error.status ?? 500}: ${error.response?.data ?? 'Error adding custom DOPs'}`
        );
        setOpen((prevState) => ({ ...prevState, errorSnackbar: true }));
      })
      .finally(() => {
        setOpen((prevState) => ({ ...prevState, selectorDialog: false }));
        setSelectedDopIds([]);
        setState(true);
        setLoading(false);
      });
  }, [
    applicationId,
    customerId,
    fetchCustomDops,
    fetchSelectorCustomDops,
    selectedDopIds,
  ]);

  /**
   * Remove a custom DOP from the application
   */
  const removeDop = useCallback(
    async (param: number) => {
      setLoading(true);
      await ApiAppCustomDop.destroy(customerId, applicationId, param)
        .then(() => {
          setOpen({ ...open, deleteDialog: false });
          fetchCustomDops();
          fetchSelectorCustomDops();
        })
        .catch((error) => {
          setError(
            `Error ${error.status ?? 500}: ${error.response?.data?.message ?? 'Error deleting custom DOPs'}`
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [applicationId, customerId, fetchCustomDops, fetchSelectorCustomDops, open]
  );

  const DeleteDialog = (
    <Dialog
      onClose={() => setOpen({ ...open, deleteDialog: false })}
      open={open.deleteDialog}
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '0.8rem',
        },
      }}
    >
      <DialogContent>
        <ConfirmContent
          onSaveClick={() => {
            removeDop(customDopToRemove as number);
            setOpen({ ...open, deleteDialog: false });
          }}
          onDiscardClick={() => setOpen({ ...open, deleteDialog: false })}
        />
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <Container
        sx={{
          gap: '1rem',
          display: 'flex',
          flexDirection: 'column',
          paddingX: '1rem !important',
          maxWidth: '100% !important',
        }}
      >
        <Typography
          variant="h5"
          sx={{ paddingY: '0 !important', paddingLeft: '0 !important' }}
        >
          {t('applications.detail.custom_dop.title')}
        </Typography>
        <Stack direction="row" pb={'1rem'} justifyContent="flex-end">
          <LoadingButton
            loading={loading}
            onClick={() => setOpen({ ...open, selectorDialog: true })}
            disabled={selectorCustomDops.length === 0}
            color="secondary"
            variant="outlined"
          >
            {t('applications.detail.custom_dop.add')}
          </LoadingButton>
        </Stack>
      </Container>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <DataGridPremium
          sx={{
            marginBottom: '2.5rem',
            marginX: '1rem',
          }}
          autoHeight={true}
          getRowId={(row) => row.id}
          rows={customDops}
          columns={columns}
          rowCount={rowCount}
          onRowClick={(params) => navigate(`/custom_dops/${params.row.id}`)}
          paginationMode="server"
          pagination={true}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      </div>
      <Snackbar
        sx={{
          '& .MuiPaper-root': {
            padding: '1rem !important',
          },
        }}
        open={open.copySnackbar}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="info">
          {t('copied_message')}
        </Alert>
      </Snackbar>
      <Dialog
        onClose={handleSelectorDialogClose}
        open={open.selectorDialog}
        fullWidth
        maxWidth="md"
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '0.5rem',
            padding: '2rem',
            paddingBottom: '3rem',
            width: '45%',
            gap: '1rem',
          },
        }}
      >
        <Typography variant="h4">
          {t('applications.detail.custom_dop.dialog_title')}
        </Typography>
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={state.toString()}
            nodeRef={nodeRef}
            addEndListener={(done: any) => {
              if (nodeRef?.current) {
                nodeRef.current.addEventListener('transitionend', done, false);
              }
            }}
            classNames="fade"
          >
            <div ref={nodeRef} className="button-container">
              {state ? (
                <>
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    marginBottom="1rem"
                  >
                    <LoadingButton
                      loading={loading}
                      disabled={selectedDopIds.length === 0}
                      variant="contained"
                      color="primary"
                      startIcon={<Save />}
                      onClick={() => setState((state) => !state)}
                    >
                      {t('save')}
                    </LoadingButton>
                  </Stack>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="custom_dop_label">
                      {t('applications.detail.custom_dop.title')}
                    </InputLabel>
                    <Select
                      labelId="custom_dop_label"
                      multiple
                      value={selectedDopIds}
                      onChange={(event) =>
                        setSelectedDopIds(event.target.value as number[])
                      }
                      input={
                        <OutlinedInput
                          label={t('applications.detail.custom_dop.title')}
                        />
                      }
                    >
                      {selectorCustomDops.map((dop: HexaAppCustomDop) => (
                        <MenuItem key={dop.id} value={dop.id}>
                          {dop.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              ) : (
                <>
                  <ConfirmContent
                    onSaveClick={onSubmit}
                    onDiscardClick={onDiscardClick}
                  />
                </>
              )}
            </div>
          </CSSTransition>
        </SwitchTransition>
      </Dialog>
      <Snackbar
        open={open.errorSnackbar}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {error}
        </Alert>
      </Snackbar>
      {DeleteDialog}
    </>
  );
};

export default AppCustomDopList;
